// Public analytics API.
// Application code imports these functions rather than touching any provider
// directly. trackEvent is fire-and-forget (errors are silently swallowed).
//
// `checkGate` resolution order (first match wins):
//   1. DevToolkit override — any user with toolkit access can flip the
//      flag locally via the floating panel (persisted in localStorage).
//   2. Statsig — the live remote value. QA and real users see the same
//      default unless an engineer explicitly sets a local override.
//      Returns `false` when the gate is unknown or Statsig is offline.
//
// The only path to enable a flag by default is the Statsig dashboard.

import { type FeatureGateName, featureGates } from '@/config/feature-gates';
import { recordAnalytics, recordAnalyticsSystem } from '@/lib/dev/statsig-registry';
import type { Network } from '@/lib/network';
import { logger } from '@/lib/telemetry/logger';
import { getDevToolkitOverride } from '@/stores/dev-toolkit';
import { createNoopProvider, NoopAnalyticsProvider } from './noop';
import type { AnalyticsProvider, ExperimentResult } from './types';

// Records into the dev-toolkit registry on both dev AND prod builds —
// engineers (`@morfi.markets` users) need to see what's flowing on
// Vercel deployments, not just local. The registry is browser-only;
// SSR no-ops via `typeof window` inside `recordAnalytics`.
const IS_BROWSER: boolean = typeof window !== 'undefined';

let provider: AnalyticsProvider = createNoopProvider();
let providerGeneration = 0;
/** The active network, set by `AnalyticsProvider` on mount and on every
 *  store change. Read by `trackEvent` to attach a `network` tag to every
 *  event payload — this is the ONLY place network flows. We deliberately
 *  do NOT push network into the Statsig user object (`custom.network`)
 *  because user identity attributes should be stable; a single user can
 *  toggle between devnet and mainnet within a session, so per-event
 *  attribution is the right granularity. */
let currentNetwork: Network | null = null;
let currentUserId: string | null = null;
export function getAnalyticsUserId(): string | null {
  return currentUserId;
}

/** Cached check used by the dev-only ledger to label every record as
 *  `sent` (real Statsig provider) vs `noop` (no client key / dev mode).
 *  Recomputed each `setProvider` so subsequent calls record the right
 *  delivery status. */
function deliveryLabel(): 'sent' | 'noop' {
  return provider instanceof NoopAnalyticsProvider ? 'noop' : 'sent';
}

export function setProvider(newProvider: AnalyticsProvider): number {
  provider = newProvider;
  providerGeneration += 1;
  if (providerReady) {
    providerReady = false;
    notifyProviderReadyListeners();
  }
  if (IS_BROWSER) {
    const kind = newProvider instanceof NoopAnalyticsProvider ? 'noop' : 'statsig';
    recordAnalyticsSystem(`provider installed: ${kind}`);
  }
  return providerGeneration;
}

/**
 * `true` once the analytics provider's gate values are usable for routing
 * decisions. Becomes `true` synchronously when the noop provider is
 * installed (local dev, or builds without a Statsig key — gates always
 * resolve to `false` so there's nothing to wait for). For Statsig it
 * flips after `StatsigClient.initializeAsync()` resolves — only at that
 * point does `provider.checkGate(name)` return real values instead of
 * the `Loading` / `Uninitialized` short-circuit (`false`).
 *
 * Feature-gated consumers must wait
 * for this signal before deciding what to render — checking the gate
 * before Statsig is ready was producing false negatives, e.g. showing
 * an unavailable feature before its assignment resolves.
 */
let providerReady: boolean = false;
const providerReadyListeners: Set<() => void> = new Set();

export function markProviderReady(generation: number): boolean {
  if (generation !== providerGeneration || providerReady) return false;
  providerReady = true;
  notifyProviderReadyListeners();
  return true;
}

/** Invalidates an in-flight provider without disturbing a newer replacement. */
export function invalidateProvider(generation: number): void {
  if (generation !== providerGeneration) return;
  providerGeneration += 1;
  provider = createNoopProvider();
  if (!providerReady) return;
  providerReady = false;
  notifyProviderReadyListeners();
}

function notifyProviderReadyListeners(): void {
  for (const listener of providerReadyListeners) {
    listener();
  }
}

/** React subscription helper compatible with `useSyncExternalStore`. */
export function subscribeProviderReady(listener: () => void): () => void {
  providerReadyListeners.add(listener);
  return (): void => {
    providerReadyListeners.delete(listener);
  };
}

export function getProviderReady(): boolean {
  return providerReady;
}

export function checkGate(name: FeatureGateName): boolean {
  const providerGateName = featureGates[name].name;

  const devOverride = getDevToolkitOverride(name);
  const source = devOverride === undefined ? 'provider' : 'override';
  const result = devOverride ?? provider.checkGate(providerGateName);

  if (IS_BROWSER) {
    recordAnalytics({
      kind: 'gate',
      delivery: source === 'provider' ? deliveryLabel() : 'noop',
      name,
      network: currentNetwork,
      payload: { result, source },
    });
  }
  return result;
}

export function trackEvent(name: string, properties: Record<string, unknown>): void {
  // Auto-tag every event with the platform and active network so analytics
  // can segment web vs mobile and devnet vs mainnet without callsites doing
  // it manually. Mobile stamps `platform: 'mobile'` in its own metadata
  // builder (mobile/src/lib/product-analytics/context.ts) — the two values
  // together drive the combined/web/mobile KPI dashboard split.
  const enriched: Record<string, unknown> = {
    ...properties,
    platform: 'web',
    ...(currentNetwork !== null ? { network: currentNetwork } : {}),
    ...(name.startsWith('trade_') && typeof properties.network === 'string'
      ? { network: properties.network }
      : {}),
  };
  try {
    provider.trackEvent(name, enriched);
  } catch (err: unknown) {
    logger.warn('Analytics trackEvent failed (fire-and-forget)', {
      eventName: name,
      error: err instanceof Error ? err.message.slice(0, 200) : String(err),
    });
  }
  if (IS_BROWSER) {
    recordAnalytics({
      kind: 'event',
      delivery: deliveryLabel(),
      name,
      network: currentNetwork,
      payload: enriched,
    });
  }
}

export function getConfig(name: string): Record<string, unknown> {
  const result = provider.getConfig(name);
  if (IS_BROWSER) {
    recordAnalytics({
      kind: 'config',
      delivery: deliveryLabel(),
      name,
      network: currentNetwork,
      payload: result,
    });
  }
  return result;
}

export function getExperiment(name: string): ExperimentResult {
  const result = provider.getExperiment(name);
  if (IS_BROWSER) {
    recordAnalytics({
      kind: 'experiment',
      delivery: deliveryLabel(),
      name,
      network: currentNetwork,
      payload: { value: result.value, ruleID: result.ruleID },
    });
  }
  return result;
}

export function identifyUser(userId: string, attributes: Record<string, unknown>): void {
  // Network is intentionally NOT enriched into user attributes — it's a
  // per-event/session signal (see `currentNetwork` doc). The Statsig
  // provider receives only stable user identity here.
  provider.identifyUser(userId, attributes);
  currentUserId = userId;
  if (IS_BROWSER) {
    recordAnalytics({
      kind: 'identify',
      delivery: deliveryLabel(),
      name: userId,
      network: currentNetwork,
      payload: attributes,
    });
  }
}

/** Update the active network used by `trackEvent` to tag every event
 *  payload. Called by `AnalyticsProvider` on mount and on every store
 *  change. Does NOT re-identify the user — network is a per-event
 *  attribute, not part of stable user identity. A single user can
 *  toggle between devnet and mainnet within one Statsig session, so
 *  recording it per event (rather than mutating the user object) is
 *  the correct granularity for analytics segmentation. */
export function setAnalyticsNetwork(network: Network): void {
  const previous = currentNetwork;
  currentNetwork = network;
  if (IS_BROWSER) {
    recordAnalyticsSystem(
      previous !== null ? `network change: ${previous} → ${network}` : `network init: ${network}`,
    );
  }
}

export type { AnalyticsConfig, AnalyticsProvider, ExperimentResult } from './types';
